import React, { useEffect } from 'react';
import { Box, TextField, Typography, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { ProductAutocomplete } from './';
import { DiscountSelector } from './';
import { calculateLineItem, formatDecimal } from '../../utils/calculations';
import { LINE_ITEM_GRID_COLUMNS } from './LineItemsTable';

const LineItemRow = React.memo(({
  item,
  index,
  onUpdate,
  onDelete,
  canDelete = true,
  vatExempt = false,
}) => {
  const vatPerc = vatExempt ? 0 : 23;

  // Calculate totals whenever relevant fields change
  useEffect(() => {
    if (item.qty !== undefined && item.price !== undefined && item.discount !== undefined) {
      const calculated = calculateLineItem(item.qty, item.price, item.discount, vatPerc);

      // Only update if calculated values changed
      if (
        calculated.subtotal !== item.subtotal ||
        calculated.vat !== item.vat ||
        calculated.total !== item.total
      ) {
        onUpdate(index, {
          ...item,
          ...calculated,
          vat_perc: vatPerc
        });
      }
    }
    // Deliberately narrow: only qty/price/discount/vatPerc changes should
    // trigger a recalculation. `item`/`index`/`onUpdate` are read from the
    // latest closure each render regardless; including them here would also
    // re-fire this effect on every unrelated field edit (registration,
    // part_number, description) and on every parent re-render that hands
    // down a new `onUpdate` reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.qty, item.price, item.discount, vatPerc]);

  const handleFieldChange = (field, value) => {
    onUpdate(index, {
      ...item,
      [field]: value
    });
  };

  const handleProductSelect = (product) => {
    // When product selected from autocomplete, fill in details
    onUpdate(index, {
      ...item,
      part_number: product.part_number || '',
      description: product.description || '',
      price: product.last_value ? parseFloat(product.last_value) : item.price
    });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: LINE_ITEM_GRID_COLUMNS,
        gap: 1,
        alignItems: 'center',
        mb: 1,
        p: 1,
        borderRadius: 1,
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Registration */}
      <TextField
        size="small"
        placeholder="Registration"
        value={item.registration || ''}
        onChange={(e) => handleFieldChange('registration', e.target.value.toUpperCase())}
      />

      {/* Part Number with Autocomplete */}
      <ProductAutocomplete
        field="part_number"
        value={item.part_number || ''}
        onChange={(value) => handleFieldChange('part_number', value)}
        onSelect={handleProductSelect}
        placeholder="Part #"
        size="small"
      />

      {/* Description with Autocomplete */}
      <ProductAutocomplete
        field="description"
        value={item.description || ''}
        onChange={(value) => handleFieldChange('description', value)}
        onSelect={handleProductSelect}
        placeholder="Item Description"
        size="small"
      />

      {/* Quantity */}
      <TextField
        size="small"
        type="number"
        placeholder="Qty"
        value={item.qty || ''}
        onChange={(e) => handleFieldChange('qty', parseFloat(e.target.value) || 0)}
        inputProps={{ min: 0, step: 1 }}
      />

      {/* Price - reformatted to 2 decimals only on blur, never mid-keystroke,
          so typing (e.g. "5", then ".", then "9") is never fought with. */}
      <TextField
        size="small"
        type="number"
        placeholder="Price"
        value={item.price || ''}
        onChange={(e) => handleFieldChange('price', parseFloat(e.target.value) || 0)}
        onBlur={() => {
          // Only reformat a genuinely entered value - leaves an untouched
          // row's price at 0 (falsy) so it keeps showing the placeholder
          // instead of becoming "0.00" from merely tabbing through it.
          if (item.price) {
            handleFieldChange('price', parseFloat(item.price).toFixed(2));
          }
        }}
        inputProps={{ min: 0, step: 0.01 }}
      />

      {/* Discount Selector */}
      <DiscountSelector
        value={item.discount !== undefined ? item.discount : 0}
        onChange={(value) => handleFieldChange('discount', parseFloat(value))}
        size="small"
      />

      {/* Subtotal/VAT/Total are calculated and read-only - plain text takes
          less width than a bordered TextField and reads more clearly as
          "calculated" rather than "editable". VAT % itself isn't shown per
          row anymore (always 23%, noted once for the whole table instead). */}
      <Typography variant="body2" textAlign="right" sx={{ pr: 1 }}>
        {formatDecimal(item.subtotal !== undefined ? item.subtotal : 0)}
      </Typography>

      <Typography variant="body2" textAlign="right" sx={{ pr: 1 }}>
        {formatDecimal(item.vat !== undefined ? item.vat : 0)}
      </Typography>

      <Typography variant="body2" fontWeight="bold" textAlign="right" sx={{ pr: 1 }}>
        {formatDecimal(item.total !== undefined ? item.total : 0)}
      </Typography>

      {/* Delete Button */}
      <IconButton
        size="small"
        onClick={() => onDelete(index)}
        disabled={!canDelete}
        color="error"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

export default LineItemRow;
